/**
 * client/src/lib/guardian-answer-digests.ts
 *
 * The client half of the guardian knowledge proof: raw answers → salted
 * SHAKE256 digests (Rule 1 / L0-16). Raw text never leaves the device (Rule 5).
 *
 * ONE implementation, because the pairing it encodes is the whole proof and a
 * mistake in it is invisible: the server holds only digests, so a wrong salt or
 * a wrong canonical form is indistinguishable from a wrong answer and silently
 * costs the guardian one of their three attempts. Both recipient surfaces — the
 * in-app accept and the pre-account invite ceremony — call this.
 *
 * `canonVersion` is as load-bearing as `salt`: the ward's digest was computed
 * under a specific fold, and folding differently misses it exactly as surely as
 * a fresh salt would. The two devices are independently deployed bundles
 * separated by however long the invite sat pending, so "whatever form this
 * bundle ships" is a guess (CLAUDE.md ledger #52). Absent means v1 — see
 * canon-version.ts for why the fallback is v1 and not "current".
 *
 * EXACTLY ONE digest per question — never a list. A second accepted digest
 * would buy the client a second guess, not a second encoding (ledger #48).
 */

import { hashSeedAnswer } from "@/lib/hash";
// Submodules, not the barrel: this file is always loaded, and the barrel also re-exports the question and answer rules.
import { normalizeVerificationAnswer } from "@shared/lib/guardian-verification/normalize";
import {
  LEGACY_GUARDIAN_ANSWER_CANON_VERSION,
  type GuardianAnswerCanonVersion,
} from "@shared/lib/guardian-verification/canon-version";

export interface GuardianAnswerQuestion {
  order: 1 | 2 | 3;
  salt: string;
  canonVersion?: GuardianAnswerCanonVersion;
}

export interface GuardianAnswerDigest {
  order: 1 | 2 | 3;
  answerHashShake256: string;
}

/** Hash each raw answer with its OWN question's salt and canonical form. */
export function buildGuardianAnswerDigests(
  questions: readonly GuardianAnswerQuestion[],
  rawAnswers: readonly string[],
): GuardianAnswerDigest[] {
  return questions.map((q, i) => ({
    order: q.order,
    answerHashShake256: hashSeedAnswer(
      normalizeVerificationAnswer(
        rawAnswers[i] ?? "",
        q.canonVersion ?? LEGACY_GUARDIAN_ANSWER_CANON_VERSION,
      ),
      q.salt,
    ),
  }));
}
